import { z } from 'zod'
import type { QuotationStatus } from './quotations.js'
import type { BilingualText, PrintBankAccount } from './settings.js'

/**
 * Documents that can be shared by public link (D13): sales invoices (and debit notes), and quotations
 * (sales-documents Q11: they go to prospects before any invoice exists).
 */
export const PUBLIC_ENTITY_TYPES = ['sales_invoice', 'quotation'] as const
export type PublicEntityType = (typeof PUBLIC_ENTITY_TYPES)[number]

/**
 * POST /sales/invoices/:id/public-link and POST /sales/quotations/:id/public-link. Omitted = the
 * `documents` setting `publicLinkDays` (90 days unless the tenant changed it; 0 there = never);
 * null = never. Revoking stays the control.
 */
export const publicLinkCreateSchema = z.object({
  expiresInDays: z.number().int().min(1).max(3650).nullable().optional(),
})
export type PublicLinkCreate = z.input<typeof publicLinkCreateSchema>

/**
 * The share state of a document: its latest share link, active or not. The token is never
 * returned here: it is shown once, when the link is created. To see a URL again, revoke the link and
 * create a new one (a new token; the old printed QR stops working).
 */
export interface PublicLinkStatus {
  /** A share link exists that is neither revoked nor expired. */
  active: boolean
  createdAt: string | null
  expiresAt: string | null
  revokedAt: string | null
  lastAccessAt: string | null
  /** Minutes in which the link was opened (counting is throttled to once a minute). */
  accessCount: number
}

/**
 * The answer to creating a link: 201 with the token and full URL when a new link was made; 200 with
 * both null when the document already had an active link (its token cannot be shown again).
 */
export interface PublicLinkIssued extends PublicLinkStatus {
  token: string | null
  url: string | null
}

/**
 * GET /public/documents/:token — what anyone holding the link may see (D13 whitelist): exactly what
 * the printed invoice shows. Never internal notes, costs, user names, warehouses, record ids, or the
 * customer's phone and e-mail (ASM-042, amended by ASM-064).
 */
export interface PublicDocument {
  entityType: 'sales_invoice'
  company: {
    name: string
    nameAr: string | null
    legalName: string | null
    legalNameAr: string | null
    vatNumber: string | null
    crNumber: string | null
    address: string | null
    addressAr: string | null
    phone: string | null
    email: string | null
    /** Whether `GET /public/documents/:token/logo` has an image to serve (ASM-069). */
    hasLogo: boolean
    /**
     * Whether `GET /public/documents/:token/signature` / `…/stamp` have an image to serve: set and
     * the document is POSTED (never for a void one, settings Q3).
     */
    hasSignature: boolean
    hasStamp: boolean
  }
  document: {
    kind: 'INVOICE' | 'DEBIT_NOTE'
    number: string
    /** A VOID document keeps resolving and is shown as void. */
    status: 'POSTED' | 'VOID'
    invoiceDate: string
    dueDate: string
    /** The customer's order number or the issuer's reference, printed under the dates. */
    reference: string | null
  }
  customer: {
    /** The customer's account code, printed so a payment can quote it. */
    code: string
    name: string
    nameAr: string | null
    /** The buyer's VAT registration number (printed on tax invoices). */
    taxNumber: string | null
    address: string | null
    city: string | null
    country: string | null
  }
  currency: string
  pricesIncludeTax: boolean
  lines: {
    lineNo: number
    description: string
    quantity: string
    /** The unit a product line was sold in; null for free-text lines (absent on older copies). */
    unit?: { symbol: string; symbolAr: string | null } | null
    unitPrice: string
    discountType: 'PERCENTAGE' | 'PER_UNIT' | 'LINE_AMOUNT'
    discountValue: string
    taxRate: string
    discount: string
    net: string
    tax: string
    total: string
  }[]
  totals: {
    gross: string
    discount: string
    subtotal: string
    tax: string
    total: string
    /** Settled so far: `amountReceived` + `amountCredited`. */
    amountPaid: string
    /** Paid by the customer: receipts applied to the document. */
    amountReceived: string
    /** Credit notes applied to the document (printed as "Credited", never as paid). */
    amountCredited: string
    /** Outstanding balance; 0 for a void document. */
    balance: string
    /** Taxable amount and VAT per rate (rate with 2 decimals, highest first), as the print shows them. */
    taxByRate: { rate: string; net: string; tax: string }[]
  }
  /** The document's printed notes. */
  notes: string | null
  /** The tenant's print footer (`documents` settings group, `printFooter` / `printFooterAr`). */
  footer: { text: string | null; textAr: string | null }
  /*
   * As the print context resolves them for the document's type (an invoice or a debit note: the
   * invoice terms).
   */
  /** The resolved terms and conditions. */
  terms: BilingualText
  /**
   * The bank accounts and text the tenant prints for payment (settings Q2), without the accounts'
   * record ids.
   */
  paymentDetails: {
    bankAccounts: Omit<PrintBankAccount, 'id'>[]
    text: BilingualText
  }
}

/**
 * GET /public/documents/:token for a shared quotation (Q11) — exactly what the printed quotation
 * shows: the offer, its validity and its lines. Never internal data (user names, record ids, costs,
 * the customer's phone or e-mail), and no payment details (a quotation is not payable). A link can be
 * made for a PENDING or APPROVED quotation; once made it keeps resolving whatever happens next, showing
 * the status (a REJECTED or CONVERTED offer is no longer open) and `expired`.
 */
export interface PublicQuotationDocument {
  entityType: 'quotation'
  /** As on the invoice view; the signature and the stamp show unless the offer was rejected. */
  company: PublicDocument['company']
  document: {
    kind: 'QUOTATION'
    number: string
    status: QuotationStatus
    quotationDate: string
    validUntil: string
    /** Pending or approved and past its valid-until date (today in Asia/Muscat). */
    expired: boolean
    /** The customer's enquiry number or the issuer's reference. */
    reference: string | null
  }
  customer: PublicDocument['customer']
  currency: string
  pricesIncludeTax: boolean
  lines: PublicDocument['lines']
  totals: Pick<PublicDocument['totals'], 'gross' | 'discount' | 'subtotal' | 'tax' | 'total' | 'taxByRate'>
  /** The quotation's printed notes. */
  notes: string | null
  footer: PublicDocument['footer']
  /** The quotation terms (the `quotation` override, else the default terms). */
  terms: BilingualText
}

/** Everything GET /public/documents/:token can answer; `entityType` tells them apart. */
export type PublicDocumentView = PublicDocument | PublicQuotationDocument
